import { useCallback } from 'react';
import { nodesApi } from '@/api/nodes';
import { useResource } from '@/hooks/useResource';
import type {
  CreateNodeRequest,
  CreateNodeResponse,
  Node,
  NodeFilters,
  NodeHealthResponse,
  TestPleskRequest,
  TestPleskResponse,
  UpdateNodeRequest,
} from '@/types/node';

/**
 * Node list + node operations.
 * Pages depend on this hook instead of the axios module.
 */
export function useNodes(filters: NodeFilters = {}) {
  const { status, mode, search } = filters;

  const fetcher = useCallback(
    async (signal: AbortSignal) => {
      const response = await nodesApi.getAll({ status, mode, search }, { signal });
      return response.nodes ?? [];
    },
    [status, mode, search]
  );

  const resource = useResource<Node[]>(fetcher, { errorMessage: 'Failed to load nodes' });
  const { setData, reload } = resource;

  const patchNode = useCallback(
    (id: string, patch: Partial<Node>) => {
      setData((prev) => prev?.map((node) => (node.id === id ? { ...node, ...patch } : node)));
    },
    [setData]
  );

  /**
   * Create a node. The response only carries id/name/token, so the list is
   * reloaded to get the full record.
   */
  const createNode = useCallback(
    async (data: CreateNodeRequest): Promise<CreateNodeResponse> => {
      const created = await nodesApi.create(data);
      void reload();
      return created;
    },
    [reload]
  );

  const updateNode = useCallback(
    async (id: string, data: UpdateNodeRequest): Promise<Node> => {
      const updated = await nodesApi.update(id, data);
      setData((prev) => prev?.map((node) => (node.id === id ? updated : node)));
      return updated;
    },
    [setData]
  );

  const deleteNode = useCallback(
    async (id: string): Promise<void> => {
      await nodesApi.delete(id);
      setData((prev) => prev?.filter((node) => node.id !== id));
    },
    [setData]
  );

  /**
   * Run a health check. On failure the node is shown offline and the error rethrown.
   */
  const checkHealth = useCallback(
    async (id: string): Promise<NodeHealthResponse> => {
      try {
        const health = await nodesApi.checkHealth(id);
        patchNode(id, {
          status: health.status,
          ...(health.agent_version ? { agent_version: health.agent_version } : {}),
        });
        // Pick up last_heartbeat and other server-side changes
        void reload();
        return health;
      } catch (error) {
        patchNode(id, { status: 'offline' });
        throw error;
      }
    },
    [patchNode, reload]
  );

  /**
   * Rotate the agent API token; resolves with the new token (shown once)
   */
  const rotateAgentToken = useCallback(
    async (id: string): Promise<string> => {
      const { token } = await nodesApi.rotateAgentToken(id);
      patchNode(id, { has_agent_token: true });
      return token;
    },
    [patchNode]
  );

  const testPlesk = useCallback(
    (data: TestPleskRequest): Promise<TestPleskResponse> => nodesApi.testPlesk(data),
    []
  );

  return {
    nodes: resource.data ?? [],
    isLoading: resource.isLoading,
    error: resource.error,
    reload,
    createNode,
    updateNode,
    deleteNode,
    checkHealth,
    rotateAgentToken,
    testPlesk,
  };
}
